import React from 'react';
import { ArrowUpRight } from 'lucide-react';

export const AbilityBrandLogo: React.FC<{
  variant?: 'dark' | 'light' | 'yellow';
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ variant = 'light', className = '', size = 'md' }) => {
  const isDark = variant === 'dark';
  const isYellow = variant === 'yellow';

  const textColor = isDark ? 'text-clarvos-black' : isYellow ? 'text-clarvos-black' : 'text-white';
  const arcColor = isDark ? '#1c1b1f' : isYellow ? '#1c1b1f' : '#ffffff';
  const accentColor = '#ffd64e';

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  const textSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  return (
    <div className={`flex items-center gap-2.5 select-none font-bold tracking-tight ${textColor} ${className}`}>
      <svg
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${iconSizes[size]} transition-transform duration-500 group-hover:scale-105`}
      >
        {/* Triple concentric arcs representing clinical resonance & agentic empowerment */}
        <circle cx="20" cy="20" r="18" stroke={arcColor} strokeWidth="2.5" strokeDasharray="30 20" strokeLinecap="round" />
        <circle cx="20" cy="20" r="12" stroke={accentColor} strokeWidth="2.5" strokeDasharray="25 15" strokeLinecap="round" transform="rotate(45 20 20)" />
        <circle cx="20" cy="20" r="6" fill={arcColor} />
      </svg>
      <div className="flex items-center gap-1.5">
        <span className={`${textSizes[size]} font-extrabold tracking-tighter`}>AbilityOS</span>
        <span className="w-1.5 h-1.5 rounded-full bg-clarvos-yellow inline-block animate-pulse"></span>
      </div>
    </div>
  );
};

export const SunIcon: React.FC<{ className?: string; size?: number }> = ({ className = '', size = 28 }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#ffd64e"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`inline-block align-middle animate-[spin_12s_linear_infinite] ${className}`}
    >
      <circle cx="12" cy="12" r="4" fill="#ffd64e" fillOpacity="0.2" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
};

export const ClarvosPillBadge: React.FC<{
  number?: string;
  label: string;
  icon?: React.ReactNode;
  variant?: 'dark' | 'light' | 'yellow';
  className?: string;
}> = ({ number, label, icon, variant = 'dark', className = '' }) => {
  const bgStyles = {
    dark: 'bg-clarvos-black text-white',
    light: 'bg-white text-clarvos-black border border-clarvos-gray-300 shadow-sm',
    yellow: 'bg-clarvos-yellow text-clarvos-black',
  };

  return (
    <div
      className={`inline-flex items-center gap-2.5 px-4 py-2 rounded-full font-bold text-xs tracking-wider uppercase transition-all duration-300 hover:scale-105 ${bgStyles[variant]} ${className}`}
    >
      {number && (
        <span className="text-[11px] font-mono opacity-60 mr-0.5">{number}</span>
      )}
      <span>{label}</span>
      {icon && (
        <div className="w-5 h-5 rounded-full bg-white/20 text-current flex items-center justify-center">
          {icon}
        </div>
      )}
    </div>
  );
};

export const ClarvosCTAButton: React.FC<{
  text: string;
  hoverText?: string;
  onClick?: () => void;
  href?: string;
  variant?: 'yellow' | 'dark' | 'white';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}> = ({
  text,
  hoverText,
  onClick,
  href,
  variant = 'yellow',
  size = 'md',
  className = '',
}) => {
  const isYellow = variant === 'yellow';
  const isDark = variant === 'dark';

  const baseColors = isYellow
    ? 'bg-clarvos-yellow text-clarvos-black hover:bg-clarvos-yellow-hover'
    : isDark
    ? 'bg-clarvos-black text-white hover:bg-clarvos-charcoal'
    : 'bg-white text-clarvos-black hover:bg-clarvos-cream border border-clarvos-gray-300';

  const padSizes = {
    sm: 'px-4 py-2 text-xs',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
  };

  const content = (
    <div
      onClick={onClick}
      className={`group relative inline-flex items-center justify-between gap-3 font-bold rounded-full cursor-pointer overflow-hidden transition-all duration-500 shadow-sm hover:shadow-md select-none ${baseColors} ${padSizes[size]} ${className}`}
    >
      <div className="relative overflow-hidden h-[1.5em] leading-[1.5em]">
        <div className="flex flex-col transition-transform duration-500 ease-clarvos group-hover:-translate-y-1/2">
          <span className="h-[1.5em] leading-[1.5em] block">{text}</span>
          <span className="h-[1.5em] leading-[1.5em] block text-current font-extrabold">{hoverText || text}</span>
        </div>
      </div>

      <div className="w-7 h-7 rounded-full bg-black/10 group-hover:bg-black/20 text-current flex items-center justify-center transition-transform duration-500 ease-clarvos group-hover:rotate-45 group-hover:scale-110">
        <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  );

  if (href) {
    return <a href={href} className="inline-block">{content}</a>;
  }
  return content;
};
